<template>
  <main class="container">
    <h1 class="page-title" data-testid="page-title">Tell Us About You</h1>

    <BackButton class="back-link" />

    <form
      class="form personal-form"
      novalidate
      @submit.prevent="formSubmit"
      autocomplete="on"
    >
      <fieldset class="form__fieldset">
        <div class="form-item" :class="{ '--has-error': $v.leadType.$error }">
          <label class="form__label">
            <span class="form__label-text">Which Describes You?</span>
            <FormError
              v-if="$v.leadType.$error"
              data-testid="lead-type-error"
              msg="Please select an option"
            />
          </label>
          <div class="form__radiobuttons">
            <label
              for="studentLead"
              data-testid="student-lead"
              :class="{ active: leadType === 'Student' }"
            >
              <input
                type="radio"
                id="studentLead"
                name="leadType"
                v-model="leadType"
                value="Student"
                required
              />
              Student
            </label>
            <label
              for="notStudentLead"
              data-testid="notStudentLead"
              :class="{ active: leadType === 'Not a Student' }"
            >
              <input
                type="radio"
                id="notStudentLead"
                name="leadType"
                v-model="leadType"
                value="Not a Student"
                required
              />
              Not A Student
            </label>
          </div>
        </div>
        <div class="form-item" :class="{ '--has-error': $v.firstname.$error }">
          <label class="form__label" for="fname">
            <span class="form__label-text">First name</span>
            <FormError
              v-if="$v.firstname.$error && !$v.firstname.required"
              data-testid="first-name-error"
              msg="First name required"
            />
          </label>
          <input
            class="form__input"
            type="text"
            id="fname"
            data-testid="first-name"
            name="fname"
            autocomplete="given-name"
            v-model.trim="firstname"
            @blur="capitaliseName"
          />
        </div>

        <div class="form-item" :class="{ '--has-error': $v.lastname.$error }">
          <label class="form__label" for="lname">
            <span class="form__label-text">Last name</span>
            <FormError
              v-if="$v.lastname.$error && !$v.lastname.required"
              msg="Last name required"
              data-testid="last-name-error"
            />
          </label>
          <input
            class="form__input"
            type="text"
            id="lname"
            data-testid="last-name"
            name="lname"
            required
            autocomplete="family-name"
            v-model.trim="lastname"
            @blur="capitaliseName"
          />
        </div>

        <email-input
          name="email"
          label="Email address"
          v-model.trim="email"
          :v="$v.email"
        />

        <div class="form-item">
          <label class="form__label" for="tel">
            <span class="form__label-text">Mobile number</span>
            <FormError
              v-if="$v.tel.$error && !$v.tel.required"
              msg="Mobile number required"
              data-testid="phone-number-required-error"
            />
            <FormError
              v-if="$v.tel.$error && !$v.tel.phoneNumber"
              msg="Mobile number is invalid"
              data-testid="phone-number-format-error"
            />
          </label>
          <input
            class="form__input"
            type="tel"
            id="tel"
            data-testid="mobile-number"
            name="tel"
            required
            autocomplete="tel"
            v-model.trim="tel"
          />
        </div>
      </fieldset>

      <button
        class="btn btn--big btn--full-width form__submit"
        data-testid="next-button"
        type="submit"
        @click="trackSubmitBtn"
      >
        Next
      </button>

      <InfoBox class="full-width">
        <template v-slot:image>
          <CustomerIcon class="fill-brand-blue" />
        </template>
        <template v-slot:title>
          Every Housemate Is Equal
        </template>
        <template v-slot>
          Your housemates will add their details later and we won’t set up a
          thing until everyone has joined.
        </template>
      </InfoBox>
    </form>
  </main>
</template>

<script>
import { email, required } from "vuelidate/lib/validators";
import { mapMutations } from "vuex";
import phoneNumber from "@/utility/phoneNumberValidation";
import FormError from "@/components/forms/FormError";
import InfoBox from "@/components/InfoBox";
import CustomerIcon from "@/assets/svg/icon-customer.svg";
import EmailInput from "@/components/EmailInput";

export default {
  name: "PersonalDetails",
  computed: {
    leadType: {
      get() {
        return this.$store.getters.leadType;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { leadType: value });
      },
    },
    firstname: {
      get() {
        return this.$store.getters.firstName;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { firstname: value });
      },
    },
    lastname: {
      get() {
        return this.$store.getters.lastName;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { lastname: value });
      },
    },
    email: {
      get() {
        return this.$store.getters.emailAddress;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { email: value });
      },
    },
    tel: {
      get() {
        return this.$store.getters.telNumber;
      },
      set(value) {
        this.$store.commit("SET_PERSONAL_DETAILS_PROP", { tel: value });
      },
    },
  },
  methods: {
    ...mapMutations({
      capitaliseName: "CAPITALISE_NAME",
      showBackBtn: "SHOW_BACK_BTN",
    }),
    formSubmit() {
      this.$v.$touch();

      if (!this.$v.$invalid) {
        this.capitaliseName();
        this.$router.push("/tenancy");
      }
    },
    trackSubmitBtn() {
      this.trackGoogleEvent(
        "Click Next Personal",
        "Create Account",
        "Confirm personal details",
      );
    },
  },
  created() {
    this.showBackBtn();
  },
  validations() {
    return {
      leadType: {
        required,
      },
      firstname: {
        required,
      },
      lastname: {
        required,
      },
      email: {
        required,
        email,
      },
      tel: {
        required,
        phoneNumber,
      },
    };
  },
  components: {
    EmailInput,
    CustomerIcon,
    InfoBox,
    FormError,
  },
};
</script>

<style lang="scss" scoped>
main {
  max-width: 60rem;
}

.form-checkbox {
  width: $container-button-width;
  margin: auto;

  @include media("<medium") {
    width: 100%;
  }
}
</style>
